import {
  Button,
  useModalState,
  Modal,
  TextInputField
} from '@sequencehq/core-components'
import { ReactNode, useState, useCallback } from 'react'
import { useForm, type FormFields } from '@sequencehq/utils'
import { required } from '@sequencehq/utils/validation'

type TaxCategoryFields = FormFields<{
  name: string
}>
type FormValues = {
  name: TaxCategoryFields['name']['value']
}

interface Props {
  initialName: string
  onSubmit: (values: { name: string }) => Promise<void>
  trigger: ReactNode
}

const EditTaxCategoryNameModalUI = ({
  initialName,
  onSubmit,
  onClose
}: Omit<Props, 'trigger'> & { onClose: () => void }) => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showValidationErrors, setShowValidationErrors] = useState(false)
  const { fields, queries } = useForm({
    value: {
      name: initialName
    },
    showValidationErrors,
    fieldConfiguration: [
      {
        property: 'name',
        validation: [required]
      }
    ]
  })

  const canSubmit =
    queries.isValid &&
    !isSubmitting &&
    fields.name.value.trim() !== initialName.trim()

  const handleSubmit = useCallback(
    async (formValues: FormValues) => {
      if (!queries.isValid) {
        setShowValidationErrors(true)
        return
      }

      if (formValues.name && !isSubmitting) {
        setIsSubmitting(true)
        try {
          await onSubmit({ name: formValues.name })
          onClose() // Only close if the operation succeeds
        } catch (error) {
          // Error is already handled by the hook (notifications), just don't close the modal
          console.error('Failed to update tax category name:', error)
        } finally {
          setIsSubmitting(false)
        }
      }
    },
    [queries.isValid, isSubmitting, onSubmit, onClose]
  )

  const handleSaveClick = useCallback(
    () => handleSubmit(queries.formData),
    [handleSubmit, queries.formData]
  )

  return (
    <>
      <Modal.Header showCloseButton withBorder onClose={onClose}>
        Edit tax category name
      </Modal.Header>
      <Modal.Content minHeight="auto">
        <TextInputField
          label="Tax category name"
          data-testid="editTaxCategoryNameInput"
          value={fields.name.value}
          validationErrors={fields.name.validationErrors}
          onChange={e => fields.name.onChange(e.target.value)}
          placeholder="Enter tax category name..."
        />
      </Modal.Content>
      <Modal.Footer withBorder>
        <Button
          onClick={onClose}
          style={{ marginLeft: 'auto' }}
          variant="secondary"
          data-testid="editTaxCategoryName.cancel"
        >
          Cancel
        </Button>

        <Button
          variant="primary"
          type="button"
          disabled={!canSubmit || isSubmitting}
          onClick={handleSaveClick}
          data-testid="editTaxCategoryName.submit"
        >
          {isSubmitting ? 'Saving...' : 'Save'}
        </Button>
      </Modal.Footer>
    </>
  )
}

const EditTaxCategoryNameModal = ({ trigger, ...props }: Props) => {
  const { isModalOpen, openModal, closeModal } = useModalState()

  return (
    <>
      <div onClick={openModal}>{trigger}</div>

      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        data-testid="editTaxCategoryNameModal"
      >
        <EditTaxCategoryNameModalUI {...props} onClose={closeModal} />
      </Modal>
    </>
  )
}

export default EditTaxCategoryNameModal
